import { EPath } from "constants/path";
import ActivityPage from "pages/ActivityPage";
import AppointmentPage from "pages/AppointmentPage";
import BodyMeasurementsPage from "pages/BodyMeasurementsPage";
import ChatPage from "pages/Chat/ChatPage";
import DailyWellBeingScorePage from "pages/DailyWellBeingScorePage";
import GeneratedProtocolResultPage from "pages/GeneratedProtocolResultPage";
import HeartMetricsPage from "pages/HeartMetricsPage";
import HomePage from "pages/HomePage";
import InviteProviderPage from "pages/InviteProviderPage";
import LearningCertificationPage from "pages/LearningCertificationPage";
import NutritionAndHydrationPage from "pages/NutritionAndHydrationPage";
import OnboardingPage from "pages/OnboardingPage";
import AddPatientPage from "pages/Patient/AddPatientPage";
import PatientDashboard from "pages/Patient/PatientDashboard";
import PatientManagementPage from "pages/Patient/PatientManagementPage";
import PaymentManagementPage from "pages/PaymentManagementPage";
import PracticeConfigurationPage from "pages/PracticeConfigurationPage";
import PracticeDetailPage from "pages/PracticeDetailPage";
import PracticeManagementPage from "pages/PracticeManagementPage";
import PracticeProfilePage from "pages/PracticeProfilePage";
import { PrescriptionCreatePage } from "pages/PrescriptionCreatePage";
import { PrescriptionDetailPage } from "pages/PrescriptionDetailPage";
import { PrescriptionManagementPage } from "pages/PrescriptionManagementPage";
import ProviderDashboard from "pages/Provider/ProviderDashboard";
import ReproductiveHealthPage from "pages/ReproductiveHealthPage";
import { RoleManagementPage } from "pages/RoleManagementPage";
import SettingsResetPasswordPage from "pages/SettingsResetPasswordPage";
import SleepPage from "pages/SleepPage";
import TreatmentPlanPage from "pages/TreatmentPlanPage";
import UserProfilePage from "pages/UserProfilePage";
export interface RouteConfig {
  path: string;
  element: React.ComponentType<Record<string, unknown>>;
  protected?: boolean;
  requireOnboarding?: boolean;
  title?: string;
  description?: string;
}

const routeConfig: RouteConfig[] = [
  {
    path: EPath.HOME,
    element: HomePage,
    protected: true,
    title: "Dashboard",
    description: "Main dashboard",
  },
  //provider dashboard
  {
    path: EPath.PROVIDER_DASHBOARD,
    element: ProviderDashboard,
    protected: true,
    title: "Staff Dashboard",
    description: "Main provider dashboard",
  },
  //patient dashboard
  {
    path: EPath.PATIENT_DASHBOARD,
    element: PatientDashboard,
    protected: true,
    title: "Patient Dashboard",
    description: "Main patient dashboard",
  },
  //patient management
  {
    path: EPath.PATIENT_MANAGEMENT,
    element: PatientManagementPage,
    protected: true,
    title: "Patient Management",
    description: "Manage patient profiles",
  },
  //add patient
  {
    path: EPath.ADD_PATIENT,
    element: AddPatientPage,
    protected: true,
    title: "Add Patient",
    description: "Create a new patient profile",
  },
  //appointment calendar
  {
    path: EPath.APPOINTMENT,
    element: AppointmentPage,
    protected: true,
    title: "Appointment",
    description: "Appointment calendar and scheduling",
  },
  //treatment plan
  {
    path: EPath.TREATMENT_PLAN,
    element: TreatmentPlanPage,
    protected: true,
    title: "Treatment Plan",
    description: "Manage treatment plan for patients",
  },
  //personalized wellness protocol
  {
    path: EPath.PERSONALIZED_WELLNESS_PROTOCOL,
    element: TreatmentPlanPage,
    protected: true,
    title: "Personalized Wellness Protocol",
    description: "View and manage personalized wellness protocol",
  },
  //generated protocol result
  {
    path: "/treatment-plan/generated-result/:patientId/:treatmentId",
    element: GeneratedProtocolResultPage,
    protected: true,
    title: "Generated Protocol Result",
    description: "View details of generated treatment protocol",
  },
  //practice management
  {
    path: EPath.PRACTICE_MANAGEMENT,
    element: PracticeManagementPage,
    protected: true,
    title: "Staff management",
    description: "Manage staff and settings",
  },
  //practice profile
  {
    path: EPath.PRACTICE_PROFILE,
    element: PracticeProfilePage,
    protected: true,
    title: "Practice Profile",
    description: "View your practice profile details",
  },
  //practice detail
  {
    path: EPath.PRACTICE_DETAIL,
    element: PracticeDetailPage,
    protected: true,
    title: "Practice Details",
    description: "View practice profile details",
  },
  //invite practice
  {
    path: EPath.INVITE_PRACTICE,
    element: InviteProviderPage,
    protected: true,
    title: "Invite Practice",
    description: "Create a new practice",
  },
  //payment management
  {
    path: EPath.PAYMENT_MANAGEMENT,
    element: PaymentManagementPage,
    protected: true,
    title: "Payment Management",
    description: "Manage payments and billing",
  },
  //practice configuration
  {
    path: EPath.PRACTICE_CONFIGURATION,
    element: PracticeConfigurationPage,
    protected: true,
    title: "Practice Configuration",
    description: "Manage practice configuration and payout settings",
  },
  //prescription management
  {
    path: EPath.PRESCRIPTION_MANAGEMENT,
    element: PrescriptionManagementPage,
    protected: true,
    title: "Prescription Management",
    description: "Manage prescriptions",
  },
  //prescription Details
  {
    path: EPath.PRESCRIPTION_DETAIL,
    element: PrescriptionDetailPage,
    protected: true,
    title: "Prescription Details",
    description: "View prescription details",
  },
  //prescription Create
  {
    path: EPath.PRESCRIPTION_CREATE,
    element: PrescriptionCreatePage,
    protected: true,
    title: "Add New Prescription",
    description: "Create a new prescription",
  },
  //onboarding
  {
    path: EPath.ONBOARDING,
    element: OnboardingPage,
    protected: true,
    requireOnboarding: true,
    title: "Onboarding",
    description: "Complete your profile setup",
  },
  //user profile
  {
    path: EPath.PROFILE,
    element: UserProfilePage,
    protected: true,
    title: "My Profile",
    description: "View and edit your profile information",
  },
  //settings reset password
  {
    path: EPath.SETTINGS_RESET_PASSWORD,
    element: SettingsResetPasswordPage,
    protected: true,
    title: "Reset Password",
    description: "Update your password",
  },
  //nutrition and hydration
  {
    path: EPath.NUTRITION_HYDRATION,
    element: NutritionAndHydrationPage,
    protected: true,
    title: "Nutrition and Hydration",
    description: "View detailed nutrition and hydration data",
  },
  //daily well being score
  {
    path: EPath.DAILY_WELL_BEING_SCORE,
    element: DailyWellBeingScorePage,
    protected: true,
    title: "Daily Well Being Score",
    description: "View detailed daily well being score data",
  },
  {
    path: EPath.REPRODUCTIVE_HEALTH,
    element: ReproductiveHealthPage,
    title: "Reproductive Health",
    description: "View detailed reproductive health data",
    protected: true,
  },
  // activity
  {
    path: EPath.ACTIVITY,
    element: ActivityPage,
    protected: true,
    title: "Activity",
    description: "View detailed activity data",
  },
  // sleep
  {
    path: EPath.SLEEP,
    element: SleepPage,
    protected: true,
    title: "Sleep",
    description: "View detailed sleep data",
  },
  // heart metrics
  {
    path: EPath.HEART_METRICS,
    element: HeartMetricsPage,
    protected: true,
    title: "Vital Signs",
    description: "View detailed vital signs data",
  },
  // role management
  {
    path: EPath.ROLE_MANAGEMENT,
    element: RoleManagementPage,
    protected: true,
    title: "Role Management",
    description: "Manage roles and permissions",
  },
  //body measurements
  {
    path: EPath.BODY_MEASUREMENTS,
    element: BodyMeasurementsPage,
    protected: true,
    title: "Body Measurements",
    description: "View detailed body measurements data",
  },
  // chat
  {
    path: EPath.COMMUNICATIONS,
    element: ChatPage,
    protected: true,
    title: "Communications",
    description: "View detailed communications data",
  },
  // learning certification
  {
    path: EPath.LEARNING_CERTIFICATION,
    element: LearningCertificationPage,
    protected: true,
    title: "Provider Learning & Certification",
    description: "Access learning resources and certification programs",
  },
];
export default routeConfig;
